/**
 * Signup acknowledgement pop-up: the opener.
 *
 * TWO CUES, BELT AND BRACES (round 3, Malou 2026-08-20: "it doesn't appear, at least not
 * with my email"):
 *
 *   1. `?customer_posted=true` — Shopify's post-back param for `{% form 'customer' %}`.
 *      Kept because it is what the demo/shortcut links use, and it costs nothing.
 *   2. A sessionStorage flag written the moment either signup form actually submits.
 *      This is the PRIMARY cue: Shopify's redirect shape is not ours to rely on — what
 *      comes back can differ for a new email, an already-subscribed email, and a post
 *      that detours through the /challenge captcha page. The flag is written by us,
 *      survives exactly the reload, and does not care what the URL says.
 *
 * The flag expires after three minutes so an abandoned submission (challenge page closed,
 * tab left overnight) cannot pop the dialog onto an unrelated page view later. It stores
 * the submitted email alongside the timestamp — unused today, reserved for a future
 * membership-aware version of the dialog — and is cleared on every open.
 *
 * See the snippet's header (snippets/bokja-signup-ack.liquid) for why the pop-up itself is
 * unconditional. One module, one dialog, both signup doors (footer form and the
 * announcement-bar invite).
 *
 * A module asset, not an inline tag: module scope keeps constants off `window`, and module
 * scripts run after the DOM is parsed, so the dialog and both forms exist without a
 * readiness dance. Failure modes all degrade to the status quo ante — the footer's inline
 * success line — so the guards below just return.
 */

const FLAG = 'bokja:signup-pending';
const FLAG_TTL_MS = 3 * 60 * 1000;
const SIGNIN_FLAG = 'bokja:signin-pending';
const SIGNIN_FLAG_TTL_MS = 2 * 60 * 1000;

/**
 * DESKTOP ONLY: the floating sign-in window (Malou, 2026-08-20: "activate this overlaying
 * webpage only for desktop, so no issues will be encountered on mobile").
 *
 * The code-entry page lives on account.bokja.com and sends `frame-ancestors 'none'`, so it
 * can never be embedded in the page. What CAN happen is the OAuth-popup pattern: open it in
 * a small real browser window over the dimmed site, and reload the page behind once the
 * sign-in completes. Mobile browsers turn window.open into a full-screen tab — the illusion
 * does not exist there — so this branch is gated on a fine pointer and desktop width, and
 * phones keep the plain same-tab journey.
 *
 * The click is intercepted in the CAPTURE phase with stopPropagation, which keeps BOTH
 * other parties away from it: the `shopify-account` component (whose on-site sheet would
 * otherwise open, then navigate this page for the code step anyway) and the shared
 * bokja-account-signin.js bubble listener. If window.open is blocked, the click falls back
 * to plain navigation — the journey Malou already tested end to end.
 *
 * COVERS EVERY SIGN-IN DOOR, not just this pop-up's button (Malou, 2026-08-20, second
 * full-page URL): the account drawer's "Sign in" row ([data-bokja-account-signin]) had the
 * James-approved sheet, but the sheet only keeps the EMAIL step on-site — it then navigates
 * the whole page for the code. On desktop the floating window is the stronger execution of
 * the same intent (never leave bokja.com), so the drawer row gets it too; the drawer stays
 * open beneath, its own backdrop dimming the page. On mobile the sheet remains exactly as
 * approved. If James prefers the sheet on desktop as well, narrow the selector below.
 *
 * Completion detection, two signals, either suffices:
 *   - the window comes back to this origin (the OAuth callback returns to bokja.com):
 *     close it and reload — the visitor is signed in where they stood;
 *   - the visitor closes the window themselves: reload anyway. Signed in, the page
 *     reflects it; not signed in, a refreshed page is the worst case.
 * While the window is on account.bokja.com, reading its location throws (cross-origin) —
 * that is the "still busy" signal, swallowed on purpose.
 */
const DESKTOP = '(min-width: 990px) and (pointer: fine)';

document.addEventListener('click', (event) => {
  if (!window.matchMedia(DESKTOP).matches) return;
  const target = event.target instanceof Element ? event.target : null;
  const anchor = target && target.closest('.bokja-signup-ack__signin, [data-bokja-account-signin]');
  if (!anchor || !(anchor instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const w = 440, h = 680;
  const left = Math.max(0, Math.round((window.screen.width - w) / 2));
  const top = Math.max(0, Math.round((window.screen.height - h) / 2));
  /**
   * THE WINDOW OPENS ON THE OAUTH ROUTE, NOT /account (Malou, 2026-08-21: after the code
   * the window sat on account.bokja.com/orders and never closed).
   *
   * Why neither auto-close signal could ever fire on the old URL: `_shopify_essential` is a
   * HOST-ONLY cookie (Set-Cookie carries no Domain=), so bokja.com and account.bokja.com
   * hold two unrelated sessions. Signing in inside the window updates the ACCOUNT host's
   * cookie only; the parent's probe fetches bokja.com with the parent's own unchanged
   * cookie, so Liquid `customer` stays nil until a reload lets shop-js sync — and the
   * reload is the thing the probe was supposed to trigger. Circular, provably dead. The
   * same-hostname watch was equally dead: /account lands the window on account.bokja.com
   * and nothing ever brings it back.
   *
   * `/customer_authentication/login` is the storefront's own OAuth entry (verified live:
   * 302 to account.bokja.com/authentication/oauth/authorize with
   * redirect_uri=bokja.com/customer_authentication/callback). Same email→code UI in the
   * window, but the chain COMES HOME: the callback commits on bokja.com, which both sets
   * the STOREFRONT session cookie (the probe can now genuinely flip to "in") and puts the
   * window on this hostname (the 400ms watch fires, closes it, reloads the parent
   * signed-in). return_to only matters if both signals somehow miss — the window would be
   * left as a normal storefront page where the visitor stood, not on a stranded orders
   * page.
   */
  const url = '/customer_authentication/login?return_to=' +
    encodeURIComponent(window.location.pathname);
  let win = null;
  try {
    win = window.open(url, 'bokja-signin',
      'width=' + w + ',height=' + h + ',left=' + left + ',top=' + top);
  } catch (e) { win = null; }
  if (!win) { window.location.href = anchor.href; return; }

  // The acknowledgement may now live inside the membership pop-up (see openAck), so the
  // waiting state goes on whichever dialog actually contains this Sign in link.
  const dialog = anchor.closest('dialog') || document.getElementById('bokja-signup-ack');
  if (dialog && dialog.open) dialog.classList.add('is-waiting');

  /**
   * Completion detection, three signals, first one wins (Malou, 2026-08-20: "can we skip
   * this page?" — the window used to sit on account.bokja.com/orders after the code,
   * because the OAuth callback 302-chains through bokja.com without ever committing a
   * document this code could see):
   *   1. SESSION PROBE (the reliable one): the callback sets the customer cookie on
   *      bokja.com itself, so the parent asks its own origin — Section Rendering API,
   *      sections/bokja-auth-state.liquid — every 2.5s. The moment it says "in", the
   *      window closes before the orders page matters and the page reloads signed-in.
   *   2. The window commits a bokja.com document (return_url honoured): same finish.
   *   3. The visitor closes the window: reload anyway; worst case is a fresh page.
   */
  let watch = 0, probe = 0;
  const finish = () => {
    window.clearInterval(watch);
    window.clearInterval(probe);
    try { if (!win.closed) win.close(); } catch (e) { /* already gone */ }
    /**
     * SIGNED-IN CONFIRMATION FLAG (Malou, 2026-08-21: "Maybe there should be a
     * confirmation instead with you're succesfully logged - and a continue on the site
     * button"). Written on EVERY finish, including the abandoned-window one, because
     * this side cannot tell success from abandonment — the reload can: the confirmation
     * dialog is Liquid-gated on `customer`, so the flag only ever finds a dialog to open
     * when the session actually landed. TTL guards the stale case (reload interrupted,
     * flag replayed on a later page view).
     */
    try {
      window.sessionStorage.setItem(SIGNIN_FLAG, String(Date.now()));
    } catch (e) { /* no storage, no confirmation — the refreshed page still shows the state */ }
    window.location.reload();
  };

  watch = window.setInterval(() => {
    if (win.closed) { finish(); return; }
    try {
      if (win.location.hostname === window.location.hostname) finish();
    } catch (e) { /* cross-origin: still on account.bokja.com, keep waiting */ }
  }, 400);

  probe = window.setInterval(() => {
    fetch('/?sections=bokja-auth-state', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const html = j && j['bokja-auth-state'];
        if (typeof html === 'string' && html.indexOf('data-bokja-auth="in"') >= 0) finish();
      })
      .catch(() => { /* transient; next tick tries again */ });
  }, 2500);
}, true);

/**
 * MOBILE SIGN-IN REPLACES THE THANK-YOU, IN PLACE (Malou, 2026-08-23: "when i click the
 * sign in button i would like the thank you pop up to be replaced with a similarly looking
 * sign in pop up"). On touch/mobile the Sign in tap opens Shopify's <shopify-account>
 * sheet — now dressed as a centred house pop-up by the bokja-custom.css skin — but the
 * sheet's element lives INSIDE the thank-you dialog, so the host cannot be closed without
 * taking the sheet down with it. Instead the host gets .bokja-signin-open (visibility
 * swap, see the CSS): only the sheet shows, the backdrop stays, and when the sheet closes
 * the thank-you is exactly where the visitor left it.
 *
 * The class is driven by the sheet's ACTUAL open state, watched via a MutationObserver on
 * the component's shadow root — not by a 'close' listener on the shadow <dialog>, because
 * the component re-renders its shadow nodes and a listener pinned to one node goes stale
 * (observed while building this). The observer targets the root, so it survives
 * re-renders, and it self-heals: whatever opens or closes the sheet, the class follows.
 * If the shadow root is closed off in a future component version, nothing here runs and
 * the sheet simply opens over the thank-you — the pre-2026-08-23 behaviour, not a break.
 * Desktop never reaches this: the floating-window listener above runs in capture and
 * stops the event.
 */
document.addEventListener('click', (event) => {
  if (window.matchMedia(DESKTOP).matches) return;
  const target = event.target instanceof Element ? event.target : null;
  // Every slotted sign-in door, not just the thank-you's (Malou, 2026-08-24: "when one
  // clicks on the account icon and is not yet signed in... the sign in doesn't stay in
  // the drawer, but becomes the pop up as well"). The account drawer's row carries the
  // same data attribute, sits in the same shopify-account arrangement inside its own
  // <dialog>, and needs the same swap - the logic below was already host-generic.
  const anchor = target && target.closest('[data-bokja-account-signin], .bokja-signup-ack__signin');
  if (!anchor) return;
  const host = anchor.closest('dialog');
  const acc = anchor.closest('shopify-account');
  if (!host || !host.open || !acc || !acc.shadowRoot) return;

  // READY means open AND carrying text, not just open (Malou's second on-device report,
  // 2026-08-23: "a small white window without text appears"). The sheet's inner content
  // arrives lazily and on some devices a state presents with nothing in it - hiding the
  // thank-you against an empty sheet strands the visitor on a blank box. Until the sheet
  // has words, the thank-you stays; the observer below adds the class the moment content
  // lands (childList fires), and removes it when the sheet closes.
  const sheetReady = () => {
    const sheet = acc.shadowRoot.querySelector('dialog');
    return !!(sheet && sheet.open && sheet.textContent.trim());
  };

  /**
   * NO MARKETING CHECKBOX IN THE SIGN-IN (Malou, 2026-08-23: "can we get rid of the
   * checkbox? email me with news and offers?"). The row lives in shopify-login-form's
   * NESTED shadow with no part and no configuration attribute (probed: the element carries
   * no attributes and its class exposes no observedAttributes), so the one door is a
   * <style> appended INTO that open shadow root. Degrades clean twice over: if Shopify
   * renames the class the rule no-ops and the row simply shows again, and if the component
   * re-renders its children the observer below re-appends. Hiding is visual only - the
   * checkbox was unchecked by default, so no behaviour changes; membership marketing
   * consent is Klaviyo's ceremony, not this sheet's.
   */
  const hideConsentRow = () => {
    const lf = acc.shadowRoot.querySelector('shopify-login-form');
    const root = lf && lf.shadowRoot;
    if (!root || root.querySelector('style[data-bokja-consent-off]')) return;
    const st = document.createElement('style');
    st.setAttribute('data-bokja-consent-off', '');
    // !important: the component's adoptedStyleSheets cascade AFTER shadow <style>
    // nodes by spec, so an unmarked rule loses to their display:flex on order.
    st.textContent = '.login-form__marketing-consent{display:none!important}';
    root.append(st);
  };

  /**
   * THE HEADING READS "SIGN IN" (Malou, 2026-08-23: "remove create account as well and
   * only have sign in"). The wording is baked into Shopify's bundle (no translation
   * override exists on this shop - probed the storefront payload), so the visible text is
   * replaced by CSS in the component's own shadow: the original collapses to font-size 0
   * and an ::after carries the label. The label comes from the dialog's data-signin-label,
   * which Liquid fills from `bokja.account.signin` - the SAME key the account drawer's row
   * uses - so all four locales say their own "Sign in" and no English leaks onto /ar.
   * Screen readers still hear Shopify's fuller sentence via aria-labelledby, which stays
   * truthful: a new email in this flow does create an account. Degrades clean: if the
   * heading id changes, the rule no-ops and the full wording shows again.
   */
  const restyleHeading = () => {
    const ackEl = document.getElementById('bokja-signup-ack');
    const label = ((ackEl && ackEl.dataset.signinLabel) || '').trim();
    const root = acc.shadowRoot;
    if (!label || !root || root.querySelector('style[data-bokja-signin-label]')) return;
    const st = document.createElement('style');
    st.setAttribute('data-bokja-signin-label', '');
    const cssString = '"' + label.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
    // Georgia italic at --bt-h2: the pop-up title voice (Malou, 2026-08-24), same as the
    // invite and thank-you heads; :root custom properties reach into the shadow.
    st.textContent =
      '#dialog-heading{font-size:0!important}' +
      '#dialog-heading::after{content:' + cssString + ';font-family:Georgia,\'Times New Roman\',serif;font-style:italic;font-weight:400;font-size:var(--bt-h2,19px);letter-spacing:0;text-transform:none}';
    root.append(st);
  };

  acc.bokjaSheetHost = host;
  if (!acc.bokjaSheetWatch) {
    acc.bokjaSheetWatch = new MutationObserver(() => {
      hideConsentRow();
      restyleHeading();
      if (acc.bokjaSheetHost) acc.bokjaSheetHost.classList.toggle('bokja-signin-open', sheetReady());
    });
    acc.bokjaSheetWatch.observe(acc.shadowRoot, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['open'],
    });
  }

  // The component opens its sheet synchronously off this same click; by bubble time it is
  // usually already open. The rAF retry covers a component version that defers a frame.
  const engage = () => {
    hideConsentRow();
    restyleHeading();
    if (sheetReady()) host.classList.add('bokja-signin-open');
  };
  engage();
  if (!host.classList.contains('bokja-signin-open')) window.requestAnimationFrame(engage);
});

const isSignupForm = (form) =>
  form instanceof HTMLFormElement &&
  /\/contact\b/.test(form.getAttribute('action') || '') &&
  form.querySelector('input[name="contact[email]"]');

/**
 * KLAVIYO-OWNED SIGNUP (Malou, 2026-08-20, "please go for it"). The membership forms keep
 * their Shopify markup and action, but a live module intercepts the submit and posts the
 * email to Klaviyo's client subscriptions API instead: no page reload, the acknowledgement
 * dialog opens on the 202, and Klaviyo owns the whole ceremony (double opt-in email → list
 * → Welcome) with no cross-system handoff. This is what kills the reload problem at the
 * root — the flag/param cues below survive only as the FALLBACK path's openers.
 *
 * Fallbacks, in order: config missing or storage of any kind unavailable → native Shopify
 * POST with the pending flag (reload, pop-up on return). Klaviyo answers anything but 202,
 * or the network fails → same. The fallback re-submits via requestSubmit(), NOT submit():
 * submit() skips every submit listener by spec — including Shopify's captcha script, whose
 * listener is what attaches the token — so whenever Shopify demanded a token the visitor
 * landed on a raw "Missing CAPTCHA token" error and the signup was lost (found 2026-08-21;
 * behaviour-triggered on Shopify's side, so it looked intermittent). requestSubmit()
 * dispatches a real submit event; the one-shot `fallbackForm` flag below is what stops THIS
 * listener from intercepting its own fallback in a loop. GO-LIVE GATE (not enforced in
 * code): the Klaviyo↔Shopify integration must sync consent BACK to Shopify, or the hosted
 * sign-in page will re-ask members for marketing consent — verify the toggle before this
 * leaves preview.
 */
let fallbackForm = null;

/* TWO STEPS, v2 (James, 2026-09-07, after testing v1 on live: "the field drops down below,
   so you miss it. It should replace the whole email field with the name field. It should
   slide off to the left", and the thank-you opened before he could type a name). The step-1
   arrow ships as a submit so an email alone still joins without JS; once this module runs it
   becomes a plain BUTTON, so the first press fires NO submit event: nothing for Shopify's
   captcha script to hook, nothing for the listener below to post, and a repeated Enter cannot
   post either (Enter in the email field is caught here). On a well-formed email step 1 slides
   off to the left (is-leaving), then the panels swap (is-step2) and step 2 slides in where
   step 1 stood, the email echoed at its top as the way back. Rules: bokja-footer's stylesheet. */
const TWO_STEP = '[data-bokja-two-step]';
const stepOf = (el) => (el && el.closest ? el.closest(TWO_STEP) : null);
const reducedMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

function revealStep2(wrap) {
  if (!wrap || wrap.classList.contains('is-step2') || wrap.classList.contains('is-leaving')) return;
  const email = wrap.querySelector('input[name="contact[email]"]');
  if (!email) return;
  if (!email.checkValidity()) { email.reportValidity(); return; }
  const echo = wrap.querySelector('[data-bokja-email-echo]');
  if (echo) echo.textContent = email.value.trim();
  const swap = () => {
    wrap.classList.remove('is-leaving');
    wrap.classList.add('is-step2');
    const first = wrap.querySelector('input[name="contact[first_name]"]');
    if (first) first.focus({ preventScroll: true });
  };
  if (reducedMotion()) { swap(); return; }
  wrap.classList.add('is-leaving');
  window.setTimeout(swap, 270);
}

function backToStep1(wrap) {
  if (!wrap) return;
  wrap.classList.remove('is-step2', 'is-leaving');
  const email = wrap.querySelector('input[name="contact[email]"]');
  if (email) email.focus({ preventScroll: true });
}

document.querySelectorAll(TWO_STEP + ' [data-bokja-step1-next]').forEach((b) => { b.type = 'button'; });

document.addEventListener('click', (event) => {
  const el = event.target instanceof Element ? event.target : null;
  if (!el) return;
  const next = el.closest('[data-bokja-step1-next]');
  if (next) { event.preventDefault(); revealStep2(stepOf(next)); return; }
  const back = el.closest('[data-bokja-signup-back]');
  if (back) { event.preventDefault(); backToStep1(stepOf(back)); }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'contact[email]') return;
  const wrap = stepOf(input);
  if (!wrap || wrap.classList.contains('is-step2')) return;
  event.preventDefault();
  revealStep2(wrap);
});

/* A reset (this module resets the form after a successful signup) returns a two-step
   surface to step 1, so the next visitor at the same dialog meets the email alone. */
document.addEventListener('reset', (event) => {
  const form = event.target;
  if (!isSignupForm(form)) return;
  const gate = form.querySelector(TWO_STEP);
  if (gate) gate.classList.remove('is-step2', 'is-leaving');
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!isSignupForm(form)) return;
  if (form === fallbackForm) { fallbackForm = null; return; }

  /* TWO STEPS (James, 2026-09-07: "too much friction ... just have an email as the first
     step, then when people add their email the name box appears in a second step"). Every
     signup surface wraps its fields in [data-bokja-two-step] and CSS hides the names until
     is-step2. The browser has already enforced `required` on the email by the time submit
     fires, so step 1 only reveals step 2 and posts nothing: no Klaviyo call, no fallback
     POST, no pending flag until the second press. A form without the wrapper falls through. */
  const gate = form.querySelector('[data-bokja-two-step]');
  if (gate && !gate.classList.contains('is-step2')) {
    // v2: a safety net only. With the module live the step-1 arrow is a plain button and
    // step 1 never reaches submit; this fires for a surface the flip never reached (a press
    // before the module ran) and behaves exactly like the first press.
    event.preventDefault();
    revealStep2(gate);
    return;
  }

  const email = (form.querySelector('input[name="contact[email]"]').value || '').trim();
  const dialog = document.getElementById('bokja-signup-ack');
  const cfg = dialog ? dialog.dataset : null;

  const armFallback = () => {
    try {
      window.sessionStorage.setItem(FLAG, JSON.stringify({ t: Date.now(), email: email }));
    } catch (e) { /* URL cue still covers the return trip */ }
  };

  if (!cfg || !cfg.klaviyoCompany || !cfg.klaviyoList || !email) { armFallback(); return; }

  event.preventDefault();

  const fallback = () => { armFallback(); fallbackForm = form; form.requestSubmit(); };
  /**
   * CONTINUITY, NOT A SWAP (Malou, 2026-08-21: "it should stay longer on the page, so users
   * can read what it says").
   *
   * This used to close the dialog the visitor was reading and open a SECOND one in its place.
   * Both pop-ups are centred and the handover is one frame, so what a person actually sees is
   * the panel they were looking at vanishing — the box changes height and every word in it
   * changes at once. Measured on live 2026-08-21: Klaviyo answered 202 in 435ms, the
   * acknowledgement opened correctly and was still open 37s later with no timer anywhere near
   * it. Nothing was closing it early. The complaint was never duration; it was that the
   * confirmation ARRIVES as a disappearance, so it reads as failure and gets dismissed unread.
   *
   * So when a dialog is already open, the acknowledgement is rendered INSIDE it: the panel
   * never leaves the screen, and only the copy within it changes. The nodes are MOVED, not
   * cloned — `<shopify-account>` is a live custom element with a slotted anchor, and moving
   * keeps the upgraded element rather than betting on a clone re-upgrading; moving also means
   * no duplicate `bokja-signup-ack-title` id can exist. The ack dialog is left empty, which
   * costs nothing: every path that opens it (`customer_posted`, the pending flag) only runs
   * after a page load, on a fresh DOM.
   *
   * The children are moved, not the `__body` wrapper, so the host's own body padding applies
   * once instead of nesting two. `aria-labelledby` is repointed at the heading that just
   * arrived, because the id it named is being removed in the same breath.
   *
   * The footer form is not inside a dialog, so it takes the original path unchanged.
   */
  const openAck = () => {
    const host = form.closest('dialog');
    if (host && host !== dialog && host.open) {
      const body = dialog.querySelector('.bokja-signup-ack__body');
      const slot = host.querySelector('.bokja-collective-invite__body');
      if (body && slot) {
        const ackNodes = Array.from(body.childNodes);
        const hostNodes = Array.from(slot.childNodes);
        const prevLabel = host.getAttribute('aria-labelledby');

        slot.replaceChildren(...ackNodes);                 // MOVE: the form goes, the panel stays
        if (slot.querySelector('#bokja-signup-ack-title')) {
          host.setAttribute('aria-labelledby', 'bokja-signup-ack-title');
        }

        // BOTH SIDES GO BACK ON CLOSE. Moving nodes leaves two elements hollow: the pop-up has
        // no signup form, and the acknowledgement dialog has no acknowledgement. Neither shows
        // until someone reopens one of them IN THE SAME PAGE LIFE — reopen the pop-up and you
        // would find last submission's "Thank you" and no form; sign up again and the footer
        // path would raise an empty dialog. Restoring on close makes the swap borrow the nodes
        // rather than consume them, so every later opener finds what it expects. `once` because
        // the listener is rebuilt on the next successful submit, with that submission's nodes.
        host.addEventListener('close', () => {
          body.replaceChildren(...ackNodes);
          slot.replaceChildren(...hostNodes);
          if (prevLabel) host.setAttribute('aria-labelledby', prevLabel);
        }, { once: true });

        return;                                            // form is detached; nothing to reset
      }
      host.close();                                        // markup not as expected -> old behaviour
    }
    if (!dialog.open) dialog.showModal();
    form.reset();
  };

  /**
   * ONE RETRY BEFORE THE FALLBACK (Malou, 2026-08-21, after landing on
   * `/challenge?form_type=customer` from the live storefront).
   *
   * WHAT THE CHALLENGE PAGE ACTUALLY IS: `fallback()` re-submits the form natively (a POST to
   * Shopify's `/contact`), and Shopify answers a form POST it distrusts with its bot challenge.
   * So the cost of ONE unlucky Klaviyo response was a shopper staring at a CAPTCHA after asking
   * for a newsletter. The fallback still exists — it is the only thing that captures a signup
   * when Klaviyo is genuinely down, and dropping it would trade a rare bad page for a silently
   * lost member — but a transient blip should not reach it. It now has to fail TWICE.
   *
   * The retry is deliberately dumb: same payload, no backoff, one attempt. Klaviyo's client
   * endpoint is idempotent for this (it is a subscription request, not a counter), so a retry
   * after a 202 that never arrived cannot double-subscribe anyone.
   *
   * Written as (onSuccess, onFailure) rather than `.then(openAck).catch(fallback)` on purpose:
   * the chained form would run `fallback()` — a full page POST — if `openAck()` itself threw,
   * i.e. a rendering bug would navigate the visitor away AFTER their signup already succeeded.
   */
  /**
   * NAMES AND SALUTATION (Malou, 2026-08-23). Both signup forms now carry optional
   * contact[first_name]/contact[last_name] (Shopify's own field names, so the native
   * fallback POST stores them on the customer too) and a bokja_salutation radio -
   * "first_name" (default), "Mr" or "Mrs". Names go to Klaviyo as standard profile
   * attributes; a chosen title goes to properties.salutation, and its ABSENCE means
   * first-name terms. The fallback path loses only the salutation preference, never
   * the names. This is what unlocks "Dear <name>" for site signups - until today every
   * surface posted email-only and the newsletter could only say "Dear Bokjies".
   */
  const field = (name) => {
    const el = form.querySelector('input[name="' + name + '"]');
    return el && el.value ? el.value.trim() : '';
  };
  const firstName = field('contact[first_name]');
  const lastName = field('contact[last_name]');
  const salutationEl = form.querySelector('input[name="bokja_salutation"]:checked');
  const salutation = salutationEl ? salutationEl.value : 'first_name';

  // Salutation echo for the top-bar greeting (bokja-greeting.js): the Mr/Mrs preference
  // lives in Klaviyo, which the storefront cannot read back, so the device that made the
  // choice keeps the one readable copy. Written on every path (Klaviyo or fallback);
  // keyed by email so the greeting only ever upgrades for the matching signed-in customer.
  if (salutation === 'Mr' || salutation === 'Mrs') {
    try {
      window.localStorage.setItem('bokja:salutation', JSON.stringify({
        email: email, salutation: salutation, first: firstName, last: lastName, t: Date.now(),
      }));
    } catch (e) { /* no echo; the base greeting still renders */ }
  }

  const send = () => {
    const profileAttributes = {
      email: email,
      properties: { $locale_language: cfg.locale || 'en' },
    };
    if (firstName) profileAttributes.first_name = firstName;
    if (lastName) profileAttributes.last_name = lastName;
    if (salutation === 'Mr' || salutation === 'Mrs') profileAttributes.properties.salutation = salutation;
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompany), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.closest('dialog') ? 'invite dialog' : 'footer signup',
            profile: { data: { type: 'profile', attributes: profileAttributes } },
          },
          relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } },
        },
      }),
    }).then((r) => (r.status === 202 ? r : Promise.reject(new Error('klaviyo ' + r.status))));
  };

  send()
    .catch(send)                       // one retry; a blip must not cost the visitor a CAPTCHA
    .then(() => openAck(), () => fallback());
});

const params = new URLSearchParams(window.location.search);

let pending = null;
try {
  const raw = window.sessionStorage.getItem(FLAG);
  if (raw) pending = JSON.parse(raw);
} catch (e) { /* unreadable flag = no flag */ }

const flagFresh = pending && typeof pending.t === 'number' && Date.now() - pending.t < FLAG_TTL_MS;
const posted = params.get('customer_posted') === 'true';

if (posted || flagFresh) {
  const dialog = document.getElementById('bokja-signup-ack');

  if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
    dialog.showModal();

    try { window.sessionStorage.removeItem(FLAG); } catch (e) { /* already gone */ }

    if (posted) {
      params.delete('customer_posted');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash
      );
    }
  }
}

/**
 * SIGNED-IN CONFIRMATION, the flag's other half (see finish()). The flag is cleared on
 * sight, success or not: it describes the reload that just happened, and replaying it on
 * a later page view would congratulate a navigation. The dialog exists only when Liquid
 * saw `customer`, so an abandoned window's reload reads the flag, finds no dialog, and
 * shows nothing — which is the correct answer to "nothing happened".
 */
let signinAt = 0;
try {
  signinAt = Number(window.sessionStorage.getItem(SIGNIN_FLAG)) || 0;
  if (signinAt) window.sessionStorage.removeItem(SIGNIN_FLAG);
} catch (e) { /* unreadable flag = no flag */ }

if (signinAt && Date.now() - signinAt < SIGNIN_FLAG_TTL_MS) {
  const signinAck = document.getElementById('bokja-signin-ack');
  if (signinAck instanceof HTMLDialogElement && typeof signinAck.showModal === 'function' && !signinAck.open) {
    signinAck.showModal();
  }
}
